import { HttpClient } from '@angular/common/http';
import { effect, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import type * as ExcelJS from 'exceljs/dist/exceljs.min.js';
import cloneDeep from 'lodash/cloneDeep';
import { firstValueFrom } from 'rxjs';
import { DownloadService } from '../../services/download.service';
import { EStore } from '../../shared/enums/store.enum';
import { TColumn } from '../site/site-table/columns/column.model';
import { TWhiteLabelStore } from '../white-label/white-label.model';
import { generateExcelHeaders } from './local/generate-excel-headers';
import { TColumnFormatMap } from './point-export-local.service';
import { logErrorInSentry } from '../errors/response-error';
import { EIconPath } from '../../shared/enums/icons.enum';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { EFeatureFlag } from 'src/app/core/services/feature-flags-consts';
import { DEFAULT_PRIMARY_COLORS } from '../white-label/white-label-utils/white-label-defaults.const';

type TLogoExtent = { width: number; height: number };

type TLogo = { url: string; extent: TLogoExtent };

type TWorksheetLogo = { imageId: number; extent: TLogoExtent };

const SQUARE_LOGO_EXTENT: TLogoExtent = { width: 30, height: 30 };

const HORIZONTAL_LOGO_EXTENT: TLogoExtent = { width: 87, height: 30 };

const convertColour = (hexColour: string): string => 'FF' + hexColour.split('#')[1];

@Injectable({
  providedIn: 'root',
})
export class PointExportXLSXService {
  private excel_headers: string[];
  private whiteLabel: TWhiteLabelStore;
  private whiteLabelSignal: Signal<TWhiteLabelStore>;

  constructor(
    private http: HttpClient,
    private downloadService: DownloadService,
    private store: Store<{ whiteLabel: TWhiteLabelStore }>,
    private featureFlagService: FeatureFlagService,
  ) {
    this.whiteLabelSignal = toSignal(this.store.select(EStore.WHITE_LABEL), {
      initialValue: { data: null, created: null, accountId: null },
    });

    effect(() => {
      const whiteLabel = this.whiteLabelSignal();
      this.whiteLabel = cloneDeep(whiteLabel);
    });
  }

  async addWorksheetToWorkbook(data: {
    workbook: ExcelJS.Workbook;
    tableToExport: string[][];
    visibleColumns: TColumn[];
    groupDepth: number;
    columnFormatMap: TColumnFormatMap;
    accountName: string;
    siteName: string;
    userName: string;
    sheetName: string;
  }): Promise<void> {
    if (!data.tableToExport || data.tableToExport.length <= 1) {
      logErrorInSentry({
        message: 'Excel export warning: Worksheet has no data rows',
        tags: {
          sheetName: data.sheetName,
          accountName: data.accountName || 'unknown',
          siteName: data.siteName || 'unknown',
          tableRowCount: String(data.tableToExport?.length || 0),
          visibleColumnsCount: String(data.visibleColumns?.length || 0),
        },
      });
    }

    const worksheet = data.workbook.addWorksheet(data.sheetName, {
      views: [{ state: 'frozen', ySplit: 4, showGridLines: false, zoomScale: 100 }],
    });

    worksheet.addRows(data.tableToExport);
    const dataRowCount = data.tableToExport.length;

    const logo = this.getLogo();

    let worksheetLogo: TWorksheetLogo | null = null;
    try {
      const imageData = await firstValueFrom(
        this.http.get(logo.url, { responseType: 'arraybuffer' }),
      );
      worksheetLogo = {
        imageId: data.workbook.addImage({
          buffer: new Uint8Array(imageData),
          extension: 'png',
        }),
        extent: logo.extent,
      };
    } catch (e) {
      console.warn('Logo failed to load');
    }

    this.addHeadingAndFooter(
      worksheet,
      worksheetLogo,
      data.tableToExport,
      data.groupDepth,
      data.accountName,
      data.siteName,
      data.userName,
    );

    const totalCols = data.groupDepth + data.visibleColumns.length + 1;
    this.formatExcelCols(data.visibleColumns, worksheet, data.columnFormatMap, data.groupDepth);
    this.styleRow(worksheet, data.groupDepth, totalCols, 4 + dataRowCount);
    this.setColumnWidths(worksheet, data.visibleColumns, data.groupDepth);

    worksheet.properties.showGridLines = false;
  }

  private getBarColour(): string {
    const isRebrand = this.featureFlagService.getFlag(EFeatureFlag.REBRAND);
    const whiteLabelColours = this.whiteLabel?.data;

    if (whiteLabelColours) {
      return convertColour(isRebrand ? whiteLabelColours.primary950 : whiteLabelColours.primary700);
    }

    if (isRebrand) {
      return convertColour(DEFAULT_PRIMARY_COLORS.primary950);
    }

    return 'FF003440';
  }

  private getLogo(): TLogo {
    if (this.whiteLabel?.data?.logoDark) {
      return {
        url: 'api/v1/images/' + this.whiteLabel.data.logoDark + '/file/size/bounded/400',
        extent: SQUARE_LOGO_EXTENT,
      };
    }

    if (this.featureFlagService.getFlag(EFeatureFlag.REBRAND)) {
      return {
        url: EIconPath.LOGO_HORIZONTAL_COLOUR_REBRAND_PNG,
        extent: HORIZONTAL_LOGO_EXTENT,
      };
    }

    return { url: EIconPath.LOGO_PINPOINT_PNG, extent: SQUARE_LOGO_EXTENT };
  }

  private styleRow(
    worksheet: ExcelJS.Worksheet,
    groupDepth: number,
    totalCols: number,
    lastDataRow: number,
  ): void {
    const groupColor = this.whiteLabel?.data
      ? 'FF' + this.whiteLabel.data.primary100.split('#')[1]
      : 'FFEAECF0';
    const whiteColor = 'FFFFFFFF';
    const borderColor = 'FFD0D5DD';
    const thinBorder: ExcelJS.Border = { style: 'thin', color: { argb: borderColor } };
    const startingRowAfterHeading = 4;

    for (let i = startingRowAfterHeading; i <= lastDataRow; i++) {
      const row = worksheet.getRow(i);

      let isGroupRow = false;

      for (let g = 1; g <= groupDepth; g++) {
        const val = row.getCell(g).value;
        if (val !== null && val !== undefined && val !== '') {
          isGroupRow = true;
          break;
        }
      }

      if (i > startingRowAfterHeading) {
        row.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: isGroupRow ? groupColor : whiteColor },
        };
      }

      for (let c = 1; c <= totalCols; c++) {
        row.getCell(c).border = {
          top: thinBorder,
          left: thinBorder,
          bottom: thinBorder,
          right: thinBorder,
        };
      }
    }
  }

  private addHeadingAndFooter(
    worksheet: ExcelJS.Worksheet,
    logo: TWorksheetLogo | null,
    tableToExport: string[][],
    groupDepth: number,
    accountName: string,
    siteName: string,
    userName: string,
  ): void {
    const primaryColor = this.getBarColour();
    const colCount = tableToExport[0].length + groupDepth;

    worksheet.getRow(1).eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: primaryColor } };
      cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
    });

    worksheet.insertRows(1, [
      new Array(colCount).fill(''),
      Object.assign(new Array(colCount).fill(''), { 0: accountName }),
      Object.assign(new Array(colCount).fill(''), { 0: siteName }),
    ]);

    for (let i = 1; i <= 3; i++) {
      worksheet.getRow(i).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFFFFF' },
      };
    }

    if (logo !== null) {
      worksheet.addImage(logo.imageId, { tl: { col: 0, row: 0 }, ext: logo.extent });
    }
    worksheet.getRow(1).height = 30;
    worksheet.getRow(2).font = { bold: true };

    // Footer
    const footerStart = worksheet.rowCount + 1;
    worksheet.addRows([
      new Array(colCount).fill(''),
      Object.assign(new Array(colCount).fill(''), { [colCount - 1]: userName }),
      Object.assign(new Array(colCount).fill(''), { [colCount - 1]: this.getDateTime() }),
    ]);

    const footerBar = worksheet.getRow(footerStart + 1);
    footerBar.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: primaryColor } };
      cell.font = { color: { argb: 'FFFFFFFF' } };
    });
  }

  formatExcelCols(
    columns: TColumn[],
    worksheet: ExcelJS.Worksheet,
    columnFormatMap: TColumnFormatMap,
    groupDepth: number,
  ): void {
    this.excel_headers = generateExcelHeaders(columns.length + groupDepth + 1);
    for (let i = groupDepth; i < columns.length + groupDepth; i++) {
      if (columnFormatMap[i]) {
        worksheet.getColumn(this.excel_headers[i]).numFmt = columnFormatMap[i].format;
      }
    }
  }

  setColumnWidths(
    worksheet: ExcelJS.Worksheet,
    visibleColumns: TColumn[],
    groupDepth: number,
  ): void {
    for (let i = 1; i <= groupDepth; i++) {
      worksheet.getColumn(i).width = 12.5;
    }
    for (let i = 0; i < visibleColumns.length; i++) {
      worksheet.getColumn(i + groupDepth + 1).width = visibleColumns[i].width / 5;
    }
    worksheet.getColumn(visibleColumns.length + groupDepth + 1).width = 90;
  }

  async downloadWorkbook(workbook: ExcelJS.Workbook, fileName: string): Promise<void> {
    try {
      const worksheetCount = workbook.worksheets?.length || 0;

      if (worksheetCount === 0) {
        logErrorInSentry({
          message: 'Excel export failed: Workbook has no worksheets',
          tags: {
            fileName,
          },
        });
        throw new Error('Cannot export empty workbook');
      }

      const buffer = await workbook.xlsx.writeBuffer();

      if (!buffer || buffer.byteLength === 0) {
        logErrorInSentry({
          message: 'Excel export failed: Workbook buffer is empty',
          tags: {
            fileName,
            worksheetCount: String(worksheetCount),
          },
        });
        throw new Error('Generated Excel file is empty');
      }

      this.downloadService.saveFile(
        new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        fileName,
      );
    } catch (error) {
      logErrorInSentry({
        message: 'Excel export failed during workbook download',
        tags: {
          fileName,
          errorMessage: error?.message || 'Unknown error',
        },
      });
      throw error;
    }
  }

  private getDateTime(): string {
    const now = new Date();
    return `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  }
}
